import Bugsnag from '@bugsnag/js'
import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import ModalBtns from '@groovehq/internal-design-system/lib/components/ModalBtns/ModalBtns'
import MessageCard from '@groovehq/internal-design-system/lib/components/MessageCard/MessageCard'
import Field from '@groovehq/internal-design-system/lib/components/Field/Field'
import AnimatedEllipsis from '@groovehq/internal-design-system/lib/components/AnimatedEllipsis/AnimatedEllipsis'
import {
  paragraph,
  text,
  button,
} from '@groovehq/internal-design-system/lib/styles/elements'
import ChatDuoOutlined from '@groovehq/internal-design-system/lib/assets/icons/ChatDuoOutlined'
import { AdminAccessDrawer } from 'subapps/settings/components/drawers/NoAccess'
import { useDrawer } from 'ducks/drawers/hooks'
import { DRAWER_TYPE_CHANNELS_FORWRADING_INSTRUCTIONS } from 'ducks/drawers/types'
import { buildDrawerQueryParam } from 'ducks/drawers/util'
import { useChannel } from 'ducks/channels/hooks'
import { doSendVerification } from 'ducks/mailboxes/actions'
import { doBuildInboxMenuFromMailboxes } from 'ducks/folders/operations/collections'
import { doOpenSearchPageWithMailboxAndFolder } from 'ducks/channels/actions'
import { selectIsInInbox } from 'selectors/location'
import { selectMailboxFolderSearchPath } from 'selectors/search/search'

import { styles } from './styles'

const TEST_CONNECTION_STATUS_UNSENT = 'UNSENT'
const TEST_CONNECTION_STATUS_PENDING = 'PENDING'
const TEST_CONNECTION_STATUS_VERIFYING = 'VERIFYING'
const TEST_CONNECTION_STATUS_VERIFIED = 'VERIFIED'
const TEST_CONNECTION_STATUS_ERROR = 'ERROR'

function isConfirmed(state) {
  return ['active', 'confirmed'].includes(state)
}

const EmailForward = ({
  drawerResourceId: channelId,
  drawerChannelType: channelType = 'forwarding',
  drawerSource: source = 'settings',
  onClose,
  onExit,
  previousDrawer,
}) => {
  const dispatch = useDispatch()
  const timers = useRef({
    firstStage: null,
    nextStage: null,
    timeoutStage: null,
    loadChannel: null,
  })

  const isInInbox = useSelector(selectIsInInbox)
  const isPartOfWizard = !!previousDrawer
  const canSetupLater = source !== 'inbox' && isPartOfWizard

  const {
    channel,
    isLoading,
    isReloading,
    isMissing,
    loadChannel,
  } = useChannel(channelId, {
    useCachedIfAvailable: false,
  })

  const { state, forward_email_address: forwardEmailAddress, email, folders } =
    channel || {}

  const inboxLink = useSelector(reduxState =>
    selectMailboxFolderSearchPath(
      reduxState,
      channelId,
      folders ? folders[0].id : null
    )
  )

  useEffect(
    () => {
      if (isMissing) onClose()
    },
    [isMissing, onClose]
  )

  const [heading, setHeading] = useState(null)
  const [testConnectionStatus, setTestConnectionStatus] = useState(
    TEST_CONNECTION_STATUS_UNSENT
  )

  const onTestConnectionSend = useCallback(
    () => {
      setHeading(null)
      loadChannel()
      setTestConnectionStatus(TEST_CONNECTION_STATUS_PENDING)
    },
    [loadChannel]
  )

  const onTestConnectionError = useCallback(() => {
    setTestConnectionStatus(TEST_CONNECTION_STATUS_ERROR)
  }, [])

  const onVerificationSend = useCallback(() => {
    setTestConnectionStatus(TEST_CONNECTION_STATUS_VERIFYING)
  }, [])

  const onVerificationError = useCallback(() => {
    setTestConnectionStatus(TEST_CONNECTION_STATUS_ERROR)
  }, [])

  const onVerificationSuccess = useCallback(() => {
    setTestConnectionStatus(TEST_CONNECTION_STATUS_VERIFIED)
  }, [])

  useEffect(
    () => {
      if (
        isConfirmed(state) &&
        testConnectionStatus === TEST_CONNECTION_STATUS_VERIFYING
      ) {
        onVerificationSuccess()
      }
    },
    [onVerificationSuccess, state, testConnectionStatus]
  )

  useEffect(
    () => {
      if (
        isConfirmed(state) ||
        testConnectionStatus !== TEST_CONNECTION_STATUS_VERIFYING ||
        // We intentionally only load on false value because
        // the built in effect inside useChannel will load
        // the channel will the value is null
        isLoading === false ||
        isMissing ||
        timers.current.loadChannel
      )
        return

      timers.current.loadChannel = setTimeout(() => {
        timers.current.loadChannel = null
        loadChannel()
      }, 5000)
    },
    [testConnectionStatus, state, isLoading, isMissing, loadChannel]
  )

  useEffect(
    () => {
      if (testConnectionStatus === TEST_CONNECTION_STATUS_VERIFYING) {
        setHeading('Testing')

        timers.current.firstStage = setTimeout(() => {
          setHeading('Just a little while longer')
        }, 20 * 1000)

        timers.current.nextStage = setTimeout(() => {
          setHeading('Hang in there')
        }, 40 * 1000)

        timers.current.timeoutStage = setTimeout(() => {
          onTestConnectionError()
        }, 60 * 1000)
      }

      return () => {
        clearTimeout(timers.current.firstStage)
        clearTimeout(timers.current.nextStage)
        clearTimeout(timers.current.timeoutStage)
        clearTimeout(timers.current.loadChannel)
        timers.current = {
          firstStage: null,
          nextStage: null,
          timeoutStage: null,
          loadChannel: null,
        }
      }
    },
    [testConnectionStatus, onTestConnectionError]
  )

  const {
    drawerId: checkInstructionsDrawerId,
    openDrawer: openCheckInstructionsDrawer,
  } = useDrawer({
    id: null,
    type: DRAWER_TYPE_CHANNELS_FORWRADING_INSTRUCTIONS,
    closeIgnoresStack: false,
  })

  const handleCheckInstructions = useCallback(
    () => {
      openCheckInstructionsDrawer(channelId, {
        query: {
          ...buildDrawerQueryParam(
            checkInstructionsDrawerId,
            'drawerChannelType',
            channelType
          ),
        },
      })
    },
    [
      openCheckInstructionsDrawer,
      channelId,
      checkInstructionsDrawerId,
      channelType,
    ]
  )

  useEffect(
    () => {
      if (testConnectionStatus !== TEST_CONNECTION_STATUS_PENDING) return

      onVerificationSend()

      dispatch(doSendVerification(channelId)).catch(error => {
        onVerificationError()
        // eslint-disable-next-line no-console
        console.error('Unable to send the verification email', { error })
        Bugsnag.notify(error, event => {
          // eslint-disable-next-line no-param-reassign
          event.severity = 'error'

          event.addMetadata('metaData', {
            channelId,
          })
        })
      })
    },
    [
      testConnectionStatus,
      channelId,
      dispatch,
      onVerificationSend,
      onVerificationSuccess,
      onVerificationError,
    ]
  )

  const animatedHeading = useMemo(
    () => (
      <>
        {heading}
        <AnimatedEllipsis />
      </>
    ),
    [heading]
  )

  const saveBtnText = useMemo(
    () => {
      switch (testConnectionStatus) {
        case TEST_CONNECTION_STATUS_PENDING:
        case TEST_CONNECTION_STATUS_VERIFYING:
          return animatedHeading
        case TEST_CONNECTION_STATUS_VERIFIED:
          return `Take me to my ${app.t('mailbox')}`
        case TEST_CONNECTION_STATUS_ERROR:
        default:
          return 'Test connection'
      }
    },
    [testConnectionStatus, animatedHeading]
  )

  const handleRebuildExitAndRedirect = useCallback(
    () => {
      dispatch(doBuildInboxMenuFromMailboxes())
      onExit()
      dispatch(
        doOpenSearchPageWithMailboxAndFolder(
          channelId,
          folders ? folders[0].id : null
        )
      )
    },
    [dispatch, onExit, channelId, folders]
  )

  const onSaveBtnClick = useCallback(
    () => {
      if (isConfirmed(state)) {
        if (isInInbox) handleRebuildExitAndRedirect()
        return
      }
      onTestConnectionSend()
    },
    [isInInbox, onTestConnectionSend, handleRebuildExitAndRedirect, state]
  )

  const handleNeedHelpClick = useCallback(() => {
    if (window?.groove?.widget) {
      window.groove.widget.updateSettings({
        iconPosition: 'left',
      })
      // push route before opening to prevent the flicker from home screen to the new route
      window.groove.widget.pushRoute('/kb/articles/1613179027')
      window.groove.widget.open()
    }
  }, [])

  const handleForwardingVerificationHelpClick = useCallback(() => {
    if (window?.groove?.widget) {
      window.groove.widget.updateSettings({
        iconPosition: 'left',
      })
      // push route before opening to prevent the flicker from home screen to the new route
      window.groove.widget.pushRoute('/kb/articles/4956810582')
      window.groove.widget.open()
    }
  }, [])

  const openWidgetContactForm = useCallback(() => {
    if (window?.groove?.widget) {
      window.groove.widget.updateSettings({
        iconPosition: 'left',
      })
      // push route before opening to prevent the flicker from home screen to the new route
      window.groove.widget.pushRoute('/contact')
      window.groove.widget.open()
    }
  }, [])

  const drawerFooter = (
    <>
      {testConnectionStatus === TEST_CONNECTION_STATUS_ERROR && (
        <MessageCard type="negative" className="grui my-10 mx-12">
          Unable to establish a connection. Please try again, or contact&nbsp;
          {window?.groove?.widget && (
            <button css={[button.styles.link]} onClick={openWidgetContactForm}>
              support@groovehq.com
            </button>
          )}
          {!window?.groove?.widget && (
            <a href="mailto:support@groovehq.com" css={[button.styles.link]}>
              support@groovehq.com
            </a>
          )}
          &nbsp; to get help.
        </MessageCard>
      )}
      {testConnectionStatus === TEST_CONNECTION_STATUS_VERIFIED && (
        <MessageCard type="positive" className="grui mb-12 mt-10 mx-20">
          Email address connected successfully.
        </MessageCard>
      )}
      <ModalBtns
        saveBtnText={saveBtnText}
        saveBtnDisabled={
          ![
            TEST_CONNECTION_STATUS_ERROR,
            TEST_CONNECTION_STATUS_UNSENT,
            TEST_CONNECTION_STATUS_VERIFIED,
          ].includes(testConnectionStatus)
        }
        onSave={onSaveBtnClick}
        saveBtnTestId="channels-forwarding-verification-test-conn-button"
        saveBtnHref={
          testConnectionStatus === TEST_CONNECTION_STATUS_VERIFIED && !isInInbox
            ? inboxLink
            : undefined
        }
        saveBtnAs={
          testConnectionStatus === TEST_CONNECTION_STATUS_VERIFIED && !isInInbox
            ? 'a'
            : undefined
        }
        tertiaryBtnText={!!canSetupLater && "I'll do this later"}
        onClickTertiaryBtn={!!canSetupLater && onExit}
      />
    </>
  )

  return (
    <>
      <AdminAccessDrawer
        title="Connection"
        footer={drawerFooter}
        onClose={onExit}
        isLoading={isLoading && !isReloading}
        data-test-id="channels-forwarding-verification"
      >
        <div className="grui flex items-center justify-between mt-12 mb-10">
          <p
            css={[
              paragraph.styles.preflight,
              text.styles.textNormal,
              text.styles.fontSemibold,
              text.styles.textSuperDark,
            ]}
          >
            Let&apos;s setup email forwarding
          </p>
          {window?.groove?.widget && (
            <button
              type="button"
              className="grui flex items-center box-shadow-sm"
              css={[
                button.styles.preflight,
                text.styles.textSm,
                styles.helpBtn,
              ]}
              onClick={handleNeedHelpClick}
            >
              <ChatDuoOutlined className="grui opacity-70 mr-1" />
              Need help?
            </button>
          )}
          {!window?.groove?.widget && (
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://help.groovehq.com/help/getting-your-emails-into-groove-08b4e653"
            >
              <button
                type="button"
                className="grui flex items-center box-shadow-sm"
                css={[
                  button.styles.preflight,
                  text.styles.textSm,
                  styles.helpBtn,
                ]}
              >
                <ChatDuoOutlined className="grui opacity-70 mr-1" />
                Need help?
              </button>
            </a>
          )}
        </div>
        <div
          css={[text.styles.textSm, text.styles.textDark]}
          className="grui mb-12"
        >
          <p css={paragraph.styles.preflight}>
            We&apos;ll setup email forwarding to allow your emails to arrive
            from <span css={text.styles.fontMedium}>{email}</span> into Groove
            moving forward.
          </p>
          <p css={paragraph.styles.preflight} className="grui mt-11">
            Follow your email provider&apos;s instructions to forward email to
            the following Groove email address.&nbsp;
            {window?.groove?.widget && (
              <button
                css={button.styles.link}
                onClick={handleForwardingVerificationHelpClick}
              >
                Visit this article to find your provider&apos;s instructions.
              </button>
            )}
            {!window?.groove?.widget && (
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://help.groovehq.com/help/forwarding-verification"
                css={button.styles.link}
              >
                Visit this article to find your provider&apos;s instructions.
              </a>
            )}
          </p>
        </div>
        <div>
          {forwardEmailAddress && (
            <Field
              disabled
              defaultValue={forwardEmailAddress}
              css={styles.fullWidth}
              showCopy
              className="grui mb-4"
            />
          )}
          <p
            css={[
              paragraph.styles.preflight,
              text.styles.textDark,
              text.styles.italic,
            ]}
          >
            Note: only one address per {app.t('mailbox')}. You can&apos;t use
            this address with any other {app.t('mailbox')} in Groove.
          </p>
        </div>
        <MessageCard isIconHidden type="info" className="grui py-10 mt-12">
          <p css={[paragraph.styles.preflight, text.styles.fontMedium]}>
            Need a {app.t('agent')} to give you a hand?
          </p>
          <p css={paragraph.styles.preflight} className="grui mt-4 mb-11">
            Send them an email with the forwarding instructions to get your
            email address connected.
          </p>
          <button
            css={button.styles.link}
            type="button"
            onClick={handleCheckInstructions}
          >
            Send instructions
          </button>
        </MessageCard>
      </AdminAccessDrawer>
    </>
  )
}

export default EmailForward
